import _ from "lodash";
import React from "react";
import {
  GridOutlined,
  ListNumberedOutlined,
  ClassProgressSummaryOutlined,
  ClipboardInsightsOutlined,
} from "@toddle-edu/ds-icons";
import { colors, getCloudUrl } from "Constants";
import {
  EQUAL_WEIGHTS,
  WEIGHTS_PROPORTIONAL,
  CUSTOM_WEIGHTS,
  ABSOLUTE_WEIGHTS,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_UBD,
  CURRICULUM_TYPE_DP,
} from "Constants/stringConstants";
import ACLStore from "lib/aclStore";
import { getIsNewAssessmentSettingsEnabled } from "GradingSetup/modules/Utils";

const FINAL_GRADE_CALCULATION_TYPES = [
  "PERCENTAGE",
  "ABSOLUTE",
  "SCORE",
  "IB_GRADE",
];

const GRADE_CALCULATION_METHODS = [
  EQUAL_WEIGHTS,
  WEIGHTS_PROPORTIONAL,
  CUSTOM_WEIGHTS,
  ABSOLUTE_WEIGHTS,
];

export const GRADEBOOK_REPORTS_SETTINGS = [
  "CategoryFinalScoreDisplayType",
  "TermFinalScoreDisplayType",
  "LearningGoalsRatingType",
];

const assessmentSetupItems = [
  {
    id: "assessment_categories",
    label: "academicSetup:assessment_categories",
    subtext: "academicSetup:assessment_categories_subtext",
    icon: <GridOutlined size="xx-small" style={{ color: colors.green400 }} />,
    bgColor: colors.green950,
    permPYP: "AdminPortal:PYPScoreCategoriesV2",
    permMYP: "AdminPortal:MYPScoreCategoriesV2",
    permUBD: "AdminPortal:UBDScoreCategoriesV2",
    permDP: "AdminPortal:DPScoreCategoriesV2",
    relativeRoute: "../assessment-categories",
  },
  {
    id: "term_grade_boundaries",
    label: "academicSetup:grade_boundaries",
    subtext: "academicSetup:term_grade_boundaries_subtext",
    icon: (
      <ListNumberedOutlined size="xx-small" style={{ color: colors.pink500 }} />
    ),
    bgColor: colors.pink950,
    permDP: "AdminPortal:DPTermGradeBoundaries",
    relativeRoute: "../grade-boundaries",
  },
  {
    id: "local_grade",
    label: "common:local_grade_text",
    subtext: "academicSetup:local_grade_subtext",
    subtextUBD: "academicSetup:local_grade_subtext_ubd",
    icon: (
      <ClassProgressSummaryOutlined
        size="xx-small"
        style={{ color: colors.blue500 }}
      />
    ),
    permMYP: "AdminPortal:MYPLocalGrades",
    permDP: "AdminPortal:DPLocalGrades",
    permUBD: "AdminPortal:UBDLocalGrades",
    bgColor: colors.blue950,
    relativeRoute: "../local-grades",
  },
  {
    id: "overall_grade_setup",
    label: "academicSetup:overall_grade_setup",
    subtext: "academicSetup:overall_grade_setup_description",
    icon: (
      <ListNumberedOutlined size="xx-small" style={{ color: colors.pink500 }} />
    ),
    permUBD: "AdminPortal:UBDGradeScales",
    bgColor: colors.pink950,
    relativeRoute: "../overall-grade-setup",
  },
  {
    id: "evaluation_criteria",
    label: "academicSetup:evaluation_criteria",
    subtext: "academicSetup:evaluation_criteria_subtext",
    icon: (
      <ClipboardInsightsOutlined
        size="xx-small"
        style={{ color: colors.violet500 }}
      />
    ),
    bgColor: colors.violet950,
    permPYP: "AdminPortal:PYPEvaluationCriteria",
    permMYP: "AdminPortal:MYPEvaluationCriteria",
    permUBD: "AdminPortal:UBDEvaluationCriteria",
    permDP: "AdminPortal:DPEvaluationCriteria",
    relativeRoute: "../evaluation-criteria",
  },
];

export const checkIsAssessmentSetupV2Enabled = () => {
  return ACLStore.can("FeatureFlag:AssessmentSetupV2");
};

export const getGradeCalculationWeightageTypes = ({ t }) => {
  return [
    {
      id: "PERCENTAGE",
      label: t("academicSetup:in_percentages"),
      subtext: t("academicSetup:percentage_weightage_subtext"),
    },
    {
      id: "ABSOLUTE",
      label: t("academicSetup:in_absolute_numbers"),
      subtext: t("academicSetup:absolute_weightage_subtext"),
    },
  ];
};

export const getFinalGradeCalculationOptions = ({ isCurriculumDP, t }) => {
  const IBGradeOption = isCurriculumDP
    ? [
        {
          id: "IB_GRADE",
          label: t("common:ib_grade"),
          subtext: t("academicSetup:ib_grade_calculation_subtext"),
        },
      ]
    : [];

  return [
    {
      id: "SCORE",
      label: t("common:score"),
      subtext: t("academicSetup:score_grade_calculation_subtext"),
    },
    ...IBGradeOption,
  ];
};

export const getGradeCalculationMethods = ({
  t,
  selectedFinalGradeCalculationType,
  isAbsoluteWeightsMethodEnabled = false,
  roundOffFinalScore,
  isNewAssessmentSettingsEnabled = false,
}) => {
  const weightageType = _.toLower(selectedFinalGradeCalculationType);
  const isAssessmentSetupV2Enabled = checkIsAssessmentSetupV2Enabled();
  const isNewAssessmentSetupEnabled =
    isAssessmentSetupV2Enabled || isNewAssessmentSettingsEnabled;

  const absoluteWeightsMethod =
    isAbsoluteWeightsMethodEnabled &&
    _.isEqual(selectedFinalGradeCalculationType, "ABSOLUTE")
      ? [
          {
            id: ABSOLUTE_WEIGHTS,
            label: t("academicSetup:absolute_weights_label"),
            subtext: t("academicSetup:absolute_weights_subtext"),
            exampleTable: `${getCloudUrl()}/assets/webapp/absolute_weights_example_table.png`,
            exampleCalculation: `${getCloudUrl()}/assets/webapp/absolute_weights_example_calculation.png`,
            exampleResult: `${getCloudUrl()}/assets/webapp/absolute_weights_example_res.png`,
          },
        ]
      : [];

  const equalWeightCalculationImage = isNewAssessmentSetupEnabled
    ? `${getCloudUrl()}/assets/webapp/equal_weights_calculation.png`
    : `${getCloudUrl()}/assets/webapp/${weightageType}_equal_weights_example_calculation.png`;
  const weightsProportionalCalculationImage = isNewAssessmentSetupEnabled
    ? roundOffFinalScore
      ? `${getCloudUrl()}/assets/webapp/weights_proportional_round_calculation.png`
      : `${getCloudUrl()}/assets/webapp/weights_proportional_calculation.png`
    : `${getCloudUrl()}/assets/webapp/${weightageType}_weights_proportional_example_calculation.png`;
  const customWeightCalculationImage = isNewAssessmentSetupEnabled
    ? roundOffFinalScore
      ? `${getCloudUrl()}/assets/webapp/custom_weights_round_calculation.png`
      : `${getCloudUrl()}/assets/webapp/custom_weights_calculation.png`
    : `${getCloudUrl()}/assets/webapp/${weightageType}_custom_weights_example_calculation.png`;

  return [
    {
      id: EQUAL_WEIGHTS,
      label: t("academicSetup:equal_weights_label"),
      subtext: t("academicSetup:equal_weights_subtext"),
      exampleTable: isNewAssessmentSetupEnabled
        ? `${getCloudUrl()}/assets/webapp/grade_calculation_method_table.png`
        : `${getCloudUrl()}/assets/webapp/${weightageType}_equal_weights_example_table.png`,
      exampleCalculation: equalWeightCalculationImage,
      exampleResult: `${getCloudUrl()}/assets/webapp/${weightageType}_equal_weights_example_res.png`,
    },
    {
      id: WEIGHTS_PROPORTIONAL,
      label: t("academicSetup:weights_proportional_label"),
      subtext: t("academicSetup:weights_proportional_subtext"),
      exampleTable: isNewAssessmentSetupEnabled
        ? `${getCloudUrl()}/assets/webapp/grade_calculation_method_table.png`
        : `${getCloudUrl()}/assets/webapp/${weightageType}_weights_proportional_example_table.png`,
      exampleCalculation: weightsProportionalCalculationImage,
      exampleResult: `${getCloudUrl()}/assets/webapp/${weightageType}_weights_proportional_example_res.png`,
    },
    {
      id: CUSTOM_WEIGHTS,
      label: t("academicSetup:custom_weights_label"),
      subtext: t("academicSetup:custom_weights_subtext"),
      exampleTable: isNewAssessmentSetupEnabled
        ? `${getCloudUrl()}/assets/webapp/grade_calculation_custom_weights_table.png`
        : `${getCloudUrl()}/assets/webapp/${weightageType}_custom_weights_example_table.png`,
      exampleCalculation: customWeightCalculationImage,
      exampleResult: `${getCloudUrl()}/assets/webapp/${weightageType}_custom_weights_example_res.png`,
    },
    ...absoluteWeightsMethod,
  ];
};

const _getAssessmentSetupItems = ({
  isCalculationMethodSelected,
  curriculumType,
  finalGradeCalculationType,
}) => {
  const isIBGradeCalculationWeightageTypeSelected =
    finalGradeCalculationType === "IB_GRADE";

  let filteredItems = _.filter(assessmentSetupItems, item => {
    let perm;
    switch (curriculumType) {
      case CURRICULUM_TYPE_PYP:
        perm = _.get(item, "permPYP");
        break;
      case CURRICULUM_TYPE_MYP:
        perm = _.get(item, "permMYP");
        break;
      case CURRICULUM_TYPE_UBD:
        perm = _.get(item, "permUBD");
        break;
      case CURRICULUM_TYPE_DP:
        perm = _.get(item, "permDP");
        break;
    }
    return perm && ACLStore.can(perm);
  });

  _.forEach(filteredItems, item => {
    switch (item.id) {
      case "term_grade_boundaries":
        item.disabled = !isCalculationMethodSelected;
        item.isButtonVisible = !isIBGradeCalculationWeightageTypeSelected;
        item.buttonDisableReasonLocalesKey =
          "academicSetup:not_applicable_for_ib_grades_calculation";
        break;
      case "assessment_categories":
      case "local_grade":
        item.disabled = !isCalculationMethodSelected;
        break;
      default:
        item.disabled = false;
    }
  });

  filteredItems = _.map(filteredItems, item => {
    let subtext = "";
    switch (curriculumType) {
      case CURRICULUM_TYPE_PYP:
        subtext = _.get(item, "subtextPYP", "");
        break;
      case CURRICULUM_TYPE_MYP:
        subtext = _.get(item, "subtextMYP", "");
        break;
      case CURRICULUM_TYPE_UBD:
        subtext = _.get(item, "subtextUBD", "");
        break;
      case CURRICULUM_TYPE_DP:
        subtext = _.get(item, "subtextDP", "");
        break;
    }

    if (_.isEmpty(subtext)) {
      subtext = _.get(item, "subtext");
    }

    return { ...(item || {}), subtext };
  });

  return filteredItems;
};

export const getAssessmentSetupItems = _.memoize(
  _getAssessmentSetupItems,
  params => JSON.stringify(params)
);

const _getAssessmentSetupChildId = ({ curriculumType }) => {
  switch (curriculumType) {
    case CURRICULUM_TYPE_PYP:
      return "PYP_ASSESSMENT_SETUP";
    case CURRICULUM_TYPE_MYP:
      return "MYP_ASSESSMENT_SETUP";
    case CURRICULUM_TYPE_UBD:
      return "UBD_ASSESSMENT_SETUP";
    case CURRICULUM_TYPE_DP:
      return "DP_ASSESSMENT_SETUP";
  }
};

export const getAssessmentSetupChildId = _.memoize(
  _getAssessmentSetupChildId,
  params => JSON.stringify(params)
);

const _getIsAdminScoreV2 = ({ curriculumType }) => {
  if (getIsNewAssessmentSettingsEnabled()) {
    return ACLStore.can("AdminPortal:AssessmentCategoriesV2");
  }

  switch (curriculumType) {
    case CURRICULUM_TYPE_PYP:
      return ACLStore.can("AdminPortal:PYPScoreCategoriesV2");
    case CURRICULUM_TYPE_MYP:
      return ACLStore.can("AdminPortal:MYPScoreCategoriesV2");
    case CURRICULUM_TYPE_UBD:
      return ACLStore.can("AdminPortal:UBDScoreCategoriesV2");
    case CURRICULUM_TYPE_DP:
      return ACLStore.can("AdminPortal:DPScoreCategoriesV2");
  }
};

export const getIsAdminScoreV2 = _.memoize(_getIsAdminScoreV2, params =>
  JSON.stringify(params)
);

const _getIsScoreV2 = ({ curriculumType }) => {
  if (getIsNewAssessmentSettingsEnabled()) {
    return ACLStore.can("FeatureFlag:AssessmentCategoriesV2");
  }

  switch (curriculumType) {
    case CURRICULUM_TYPE_PYP:
      return ACLStore.can("FeatureFlag:PYPScoreCategoriesV2");
    case CURRICULUM_TYPE_MYP:
      return ACLStore.can("FeatureFlag:MYPScoreCategoriesV2");
    case CURRICULUM_TYPE_UBD:
      return ACLStore.can("FeatureFlag:UBDScoreCategoriesV2");
    case CURRICULUM_TYPE_DP:
      return ACLStore.can("FeatureFlag:DPScoreCategoriesV2");
  }
};

export const getIsScoreV2 = _.memoize(_getIsScoreV2, params =>
  JSON.stringify(params)
);

export const getGradeCalculationWeightageType = ({ configuration }) => {
  const value = configuration?.value ?? configuration?.stringValue;
  if (_.includes(FINAL_GRADE_CALCULATION_TYPES, value)) return value;
  return "PERCENTAGE";
};

const _getGradeCalculationMethod = ({ configuration }) => {
  const value = configuration?.value ?? configuration?.stringValue;
  if (_.includes(GRADE_CALCULATION_METHODS, value)) return value;
  return "";
};

export const getGradeCalculationMethod = _.memoize(
  _getGradeCalculationMethod,
  params => JSON.stringify(params)
);

export const getGradeCalculationMethodCategory = ({ curriculumType }) => {
  switch (curriculumType) {
    case CURRICULUM_TYPE_PYP:
      return "PYP_GRADE_CALCULATION_METHOD";
    case CURRICULUM_TYPE_MYP:
      return "MYP_GRADE_CALCULATION_METHOD";
    case CURRICULUM_TYPE_UBD:
      return "UBD_GRADE_CALCULATION_METHOD";
    case CURRICULUM_TYPE_DP:
      return "GRADE_CALCULATION_METHOD";
  }
};

export const getGradeCalculationWeightageTypeSetting = ({ curriculumType }) => {
  switch (curriculumType) {
    case CURRICULUM_TYPE_PYP:
      return "PYPGradeCalculationWeightageType";
    case CURRICULUM_TYPE_MYP:
      return "MYPGradeCalculationWeightageType";
    case CURRICULUM_TYPE_UBD:
      return "UBDGradeCalculationWeightageType";
    case CURRICULUM_TYPE_DP:
      return "GradeCalculationWeightageType";
  }
};

const _getGradeCalculationMethodSetting = ({ curriculumType }) => {
  if (getIsNewAssessmentSettingsEnabled()) {
    return "GradeCalculationMethod";
  }
  switch (curriculumType) {
    case CURRICULUM_TYPE_PYP:
      return "PYPGradeCalculationMethod";
    case CURRICULUM_TYPE_MYP:
      return "MYPGradeCalculationMethod";
    case CURRICULUM_TYPE_UBD:
      return "UBDGradeCalculationMethod";
    case CURRICULUM_TYPE_DP:
      return "GradeCalculationMethod";
  }
};

export const getGradeCalculationMethodSetting = _.memoize(
  _getGradeCalculationMethodSetting,
  params => JSON.stringify(params)
);

export const getDecimalSetting = ({ curriculumType }) => {
  switch (curriculumType) {
    case CURRICULUM_TYPE_PYP:
      return "PYPRoundFinalScore";
    case CURRICULUM_TYPE_MYP:
      return "MYPRoundFinalScore";
    case CURRICULUM_TYPE_UBD:
      return "UBDRoundFinalScore";
    case CURRICULUM_TYPE_DP:
      return "DPRoundFinalScore";
  }
};

const _getConfigurationByName = ({ configurations, configurationName }) => {
  return _.find(
    configurations,
    node => _.get(node, "configuration") === configurationName
  );
};

export const getConfigurationByName = _.memoize(
  _getConfigurationByName,
  params => JSON.stringify(params)
);

const _getAssessmentSetupHeaderSubtext = ({ t, curriculumType }) => {
  switch (curriculumType) {
    case CURRICULUM_TYPE_UBD:
      return t("academicSetup:assessment_setup_ubd_header_subtext");
    case CURRICULUM_TYPE_DP:
      return t("academicSetup:assessment_setup_header_subtext");
    default:
      return t("academicSetup:assessment_setup_header_subtext");
  }
};

export const getAssessmentSetupHeaderSubtext = _.memoize(
  _getAssessmentSetupHeaderSubtext,
  params => JSON.stringify(params)
);

export const getInstructionPoints = ({ selectedFinalGradeCalculationType }) => {
  switch (selectedFinalGradeCalculationType) {
    case "SCORE":
      return [
        "academicSetup:score_final_calculation_type_instruction_step_one",
        "academicSetup:score_final_calculation_type_instruction_step_two",
        "academicSetup:score_final_calculation_type_instruction_step_three",
        "academicSetup:score_final_calculation_type_instruction_step_four",
      ];
    case "IB_GRADE":
      return [
        "academicSetup:ib_grade_final_calculation_type_instruction_step_one",
        "academicSetup:ib_grade_final_calculation_type_instruction_step_two",
        "academicSetup:ib_grade_final_calculation_type_instruction_step_three",
      ];
  }
};

export const getAssessmentSettings = ({ t }) => {
  return [
    {
      label: t("academicSetup:display_category_total_as"),
      key: "CategoryFinalScoreDisplayType",
      dropdownOptions: [
        {
          key: "PERCENTAGE",
          label: t("common:total_percentage"),
        },
        {
          key: "WEIGHT",
          label: t("progressReport:number_plural"),
        },
      ],
    },
    {
      label: t("academicSetup:display_term_total_as"),
      key: "TermFinalScoreDisplayType",
      dropdownOptions: [
        {
          key: "PERCENTAGE",
          label: t("common:total_percentage"),
        },
        {
          key: "WEIGHT",
          label: t("progressReport:number_plural"),
        },
      ],
    },
    {
      label: t("academicSetup:learning_goals_rating_for_each_grading_period"),
      key: "LearningGoalsRatingType",
      dropdownOptions: [
        {
          key: "NO_CARRY_FORWARD",
          label: t(
            "academicSetup:select_blank_gradebook_for_every_grading_period"
          ),
        },
        {
          key: "CARRY_FORWARD_FROM_PREVIOUS_GRADING_PERIOD",
          label: t("academicSetup:rating_forward_from_previous_grading_period"),
        },
      ],
    },
  ];
};

export const getSettingSelectedValues = ({ configurations }) => {
  return _.reduce(
    GRADEBOOK_REPORTS_SETTINGS,
    (settingsObject, setting) => {
      settingsObject[setting] = _.get(
        getConfigurationByName({
          configurations,
          configurationName: setting,
        }),
        "value"
      );

      return settingsObject;
    },
    {}
  );
};
